(() => {
  const timers = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const interactionVideos = new Set();
  let userHasInteracted = false;

  function loadAndPlayVideo(video) {
    if (!video || (reduceMotion.matches && video.dataset.plIgnoreReducedMotion !== 'true')) return;
    // Let the first page images finish before starting decorative video downloads.
    if (document.readyState !== 'complete') {
      if (video.dataset.plVideoWaiting !== 'true') {
        video.dataset.plVideoWaiting = 'true';
        window.addEventListener('load', () => {
          delete video.dataset.plVideoWaiting;
          if (video.isConnected) loadAndPlayVideo(video);
        }, { once: true });
      }
      return;
    }
    if (video.dataset.plVideoLoaded !== 'true') {
      video.dataset.plVideoLoaded = 'true';
      video.querySelectorAll?.('source[data-pl-src]').forEach((source) => {
        source.src = source.dataset.plSrc;
        source.removeAttribute('data-pl-src');
      });
      video.load();
    }
    video.play().catch(() => {});
  }

  function activateInteractionVideos() {
    userHasInteracted = true;
    interactionVideos.forEach((video) => {
      if (!video.isConnected) {
        interactionVideos.delete(video);
        return;
      }
      loadAndPlayVideo(video);
    });
  }

  document.addEventListener('pointerdown', activateInteractionVideos, { once: true, passive: true, capture: true });
  document.addEventListener('keydown', activateInteractionVideos, { once: true, capture: true });
  window.addEventListener('scroll', activateInteractionVideos, { once: true, passive: true });

  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (!entry.isIntersecting || reduceMotion.matches) {
        video.pause();
        return;
      }
      loadAndPlayVideo(video);
    });
  }, { rootMargin: '80px 0px' });

  function initDeferredVideos(scope = document) {
    scope.querySelectorAll('[data-pl-deferred-video]').forEach((video) => {
      if (video.dataset.plVideoObserved === 'true') return;
      video.dataset.plVideoObserved = 'true';
      if (video.dataset.plVideoActivation === 'interaction') {
        interactionVideos.add(video);
        if (userHasInteracted) loadAndPlayVideo(video);
      } else if (video.dataset.plVideoActivation === 'desktop-auto-mobile-interaction') {
        if (window.matchMedia('(min-width: 750px)').matches) loadAndPlayVideo(video);
        else interactionVideos.add(video);
      } else {
        videoObserver.observe(video);
      }
    });
  }

  function initHero(root) {
    if (!root || root.dataset.plHeroAutoplayV2 === 'true') return;

    const slides = Array.from(root.querySelectorAll('[data-pl-hero-slide]'));
    const dotsContainer = root.querySelector('[data-pl-hero-dots]');
    if (!slides.length || !dotsContainer) return;

    root.dataset.plHeroAutoplayV2 = 'true';
    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const autoplayEnabled = root.dataset.autoplay !== 'false';
    const interval = Math.max(1000, Number.parseInt(root.dataset.interval, 10) || 3000);
    const pendingSlides = new Map();
    let autoplayReady = false;
    let requestedSlide = 0;

    dotsContainer.replaceChildren();

    function preloadSlide(index) {
      const normalizedIndex = (index + slides.length) % slides.length;
      if (pendingSlides.has(normalizedIndex)) return pendingSlides.get(normalizedIndex);
      const slide = slides[normalizedIndex];
      const image = slide?.querySelector('img');
      if (!image) return Promise.resolve(true);
      if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);

      // Promote the actual <picture> image: no duplicate Image or canvas request.
      const ready = new Promise((resolve) => {
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          const success = image.naturalWidth > 0;
          if (!success) pendingSlides.delete(normalizedIndex);
          resolve(success);
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        image.loading = 'eager';
      });
      pendingSlides.set(normalizedIndex, ready);
      return ready;
    }

    async function showSlide(nextIndex) {
      const request = ++requestedSlide;
      if (!(await preloadSlide(nextIndex)) || request !== requestedSlide || !root.isConnected) return;
      currentIndex = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const active = index === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.tabIndex = active ? 0 : -1;
        slide.toggleAttribute('inert', !active);
      });

      Array.from(dotsContainer.children).forEach((dot, index) => {
        const active = index === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    function stopAutoplay() {
      const activeTimer = timers.get(root);
      if (activeTimer) window.clearInterval(activeTimer);
      timers.delete(root);
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplayReady || !autoplayEnabled || slides.length < 2 || document.hidden || reduceMotion.matches) return;

      const timer = window.setInterval(() => {
        showSlide(currentIndex + 1).then(() => preloadSlide(currentIndex + 1));
      }, interval);
      timers.set(root, timer);
    }

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Banner ${index + 1}`);
      dot.addEventListener('click', () => {
        showSlide(index).then(startAutoplay);
      });
      dotsContainer.appendChild(dot);
    });

    // Keep the initial slide visible for a full interval after its image arrives.
    showSlide(currentIndex).then(() => {
      if (!root.isConnected) return;
      autoplayReady = true;
      if (autoplayEnabled && !reduceMotion.matches) preloadSlide(currentIndex + 1);
      startAutoplay();
    });
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) startAutoplay();
    });

    let touchStart = null;
    let blockBannerClick = false;

    root.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStart = { x: touch.clientX, y: touch.clientY };
      stopAutoplay();
    }, { passive: true });

    root.addEventListener('touchend', (event) => {
      if (!touchStart) return;
      const touch = event.changedTouches[0];
      if (!touch) {
        touchStart = null;
        startAutoplay();
        return;
      }
      const distanceX = touch.clientX - touchStart.x;
      const distanceY = touch.clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(distanceX) < 45 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) {
        startAutoplay();
        return;
      }

      blockBannerClick = true;
      showSlide(currentIndex + (distanceX < 0 ? 1 : -1));
      startAutoplay();
      window.setTimeout(() => { blockBannerClick = false; }, 250);
    }, { passive: true });

    root.addEventListener('touchcancel', () => {
      touchStart = null;
      startAutoplay();
    });

    root.addEventListener('click', (event) => {
      if (!blockBannerClick) return;
      event.preventDefault();
      event.stopPropagation();
      blockBannerClick = false;
    }, true);
  }

  function initAll(scope = document) {
    initDeferredVideos(scope);
    scope.querySelectorAll('[data-pl-hero-slider]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pl-hero-slider]').forEach((root) => {
      const timer = timers.get(root);
      if (timer) window.clearInterval(timer);
      timers.delete(root);
    });
    event.target.querySelectorAll('[data-pl-deferred-video]').forEach((video) => {
      interactionVideos.delete(video);
      videoObserver.unobserve(video);
    });
  });
})();
